/**
 * useOnlineStatus — current network state.
 *
 * Two signals:
 *   - navigator.onLine — fast, but lies. Returns true if your LAN is up
 *     even when the internet is down.
 *   - Heartbeat ping — actually exercises the connection. Configurable
 *     URL (defaults to a /health endpoint).
 *
 * The combined `online` flag is true ONLY when both agree.
 * `browserOnline` and `serverReachable` are exposed separately for the
 * status bar to show specific states.
 */
import { useEffect, useState } from "react";

export interface OnlineStatus {
  /** Combined: true iff browser AND last heartbeat agree we're online. */
  online: boolean;
  /** navigator.onLine raw value. */
  browserOnline: boolean;
  /** Last successful heartbeat ping. null if never attempted. */
  serverReachable: boolean | null;
  /** Unix ms of last heartbeat check. */
  lastCheckAt: number | null;
}

export interface UseOnlineOpts {
  /** Heartbeat URL. Pass null to disable heartbeat (rely only on browser). */
  heartbeatUrl?: string | null;
  /** Heartbeat interval ms. Default 30s. */
  intervalMs?: number;
  /** Heartbeat timeout ms. Default 5s. */
  timeoutMs?: number;
}

export function useOnlineStatus(opts: UseOnlineOpts = {}): OnlineStatus {
  const {
    heartbeatUrl = "/health",
    intervalMs = 30_000,
    timeoutMs = 5_000,
  } = opts;

  const [browserOnline, setBrowserOnline] = useState<boolean>(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [serverReachable, setServerReachable] = useState<boolean | null>(null);
  const [lastCheckAt, setLastCheckAt] = useState<number | null>(null);

  // ── Browser online/offline events ──────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOnline = () => setBrowserOnline(true);
    const onOffline = () => setBrowserOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // ── Heartbeat ping ─────────────────────────────────────────
  useEffect(() => {
    if (!heartbeatUrl) {
      setServerReachable(null);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const check = async () => {
      // Skip if the browser itself says offline — don't waste cycles
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        if (!cancelled) {
          setServerReachable(false);
          setLastCheckAt(Date.now());
        }
        timer = setTimeout(check, intervalMs);
        return;
      }
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), timeoutMs);
        const res = await fetch(heartbeatUrl, {
          method: "GET",
          signal: controller.signal,
          cache: "no-store",
        });
        clearTimeout(timeout);
        if (!cancelled) {
          setServerReachable(res.ok);
          setLastCheckAt(Date.now());
        }
      } catch {
        if (!cancelled) {
          setServerReachable(false);
          setLastCheckAt(Date.now());
        }
      }
      if (!cancelled) timer = setTimeout(check, intervalMs);
    };

    check();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [heartbeatUrl, intervalMs, timeoutMs]);

  const online =
    browserOnline && (serverReachable === null || serverReachable === true);

  return { online, browserOnline, serverReachable, lastCheckAt };
}
